import { initTrackingPreferences as _initTrackingPreferences } from './cmp-state';
import * as cookieStore from './cookie-store';
import {
  isRegulatedMarket,
  isBrowserEnvironment,
  setTCFEnabled,
  PREFERENCES,
} from './isomorphic';
import {
  migrateExistingUserTracking,
  provisionNonRegulatedMarketTracking,
} from './purpose-scoped-id-service';
import isWebInApp from './web-in-app';
import * as wiaStore from './wia-store';

import type { TrackingPreferencesConfig } from './ketch-types';

// Existing exports
export { isRegulatedMarket, setTCFEnabled, PREFERENCES };

const store = isWebInApp() ? wiaStore : cookieStore;

export const {
  acknowledgeTrackingSettings,
  getPreferences,
  isGdprInitSessionActive,
  isGdprInitialised,
  setInitialPreferences,
  setPreferences,
  showCookieBanner,
} = store;

/**
 * Returns whether or not non-essential tracking had been consented to
 * @param market a market (for example 'RU', 'UK', 'US', etc.)
 * @returns whether or not non-essential/optimisation/'information' tracking was enabled
 */
export const isNonEssentialTrackingEnabled = (market: string): boolean =>
  store.getPreferences(market)[PREFERENCES.INFORMATION];

export function initTrackingPreferences(
  config: TrackingPreferencesConfig,
): void {
  _initTrackingPreferences(config);
  if (config.market && isBrowserEnvironment()) {
    migrateExistingUserTracking(config.market);
    provisionNonRegulatedMarketTracking(config.market);
  }
}

// Ketch CMP integration methods (Phase 1)
export {
  getCMPSource,
  isKetchMode,
  isPurposeScopedIdServiceEnabled,
} from './cmp-state';
export { willShowConsentBanner, getConsent } from './ketch-consent';
export { loadCMPSDK } from './ketch-sdk-loader';

// Ketch CMP integration methods (Phase 2)
export {
  updateConsent,
  getConsentConfig,
  getTCString,
  isConsentBannerBeingShown,
  getVendorCount,
} from './ketch-consent-api';

// Consent action constants and Ketch purpose codes
export { CONSENT_ACTIONS, KETCH_PURPOSE_CODES } from './ketch-types';

// Types
export type {
  CMPSource,
  TrackingPreferencesConfig,
  CMPSDKConfig,
  KetchConsentCookie,
  KetchConsentData,
  KetchPurposeCode,
  ConsentAction,
  VendorConsents,
  ConsentConfig,
  Purpose,
  KetchVendor,
  KetchVendorPurpose,
  KetchDataDeclaration,
  KetchTCFFeature,
  KetchTCFSpecialPurpose,
} from './ketch-types';
